import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Registro } from '../types';
import { fetchRegistros } from '../services/api';

interface RegistrosContextType {
  registros: Registro[];
  loading: boolean;
  error: string | null;
  cargarDatos: () => Promise<void>;
  actualizarRegistro: (id: string, data: Partial<Registro>) => void;
  agregarRegistro: (registro: Registro) => void;
  eliminarRegistro: (id: string) => void;
}

const RegistrosContext = createContext<RegistrosContextType | undefined>(undefined);

export const useRegistros = () => {
  const context = useContext(RegistrosContext);
  if (!context) {
    throw new Error('useRegistros debe ser usado dentro de un RegistrosProvider');
  }
  return context;
};

interface RegistrosProviderProps {
  children: ReactNode;
}

export const RegistrosProvider: React.FC<RegistrosProviderProps> = ({ children }) => {
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarDatos = useCallback(async () => {
    // Solo cargar datos si hay token
    if (!localStorage.getItem('token')) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchRegistros();
      // Fuerza cambio de referencia clonando el array
      setRegistros(Array.isArray(data) ? [...data] : []);
      setError(null);
    } catch (err: unknown) {
      // Mostrar mensaje de error real si existe
      if (err instanceof Error && err.message) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else {
        setError('Error al cargar los registros');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const actualizarRegistro = useCallback((id: string, data: Partial<Registro>) => {
    setRegistros(prev =>
      prev.map(registro => (registro.id === id ? { ...registro, ...data } : registro))
    );
  }, []);

  const agregarRegistro = useCallback((registro: Registro) => {
    setRegistros(prev => [registro, ...prev]);
  }, []);

  const eliminarRegistro = useCallback((id: string) => {
    setRegistros(prev => prev.filter(registro => registro.id !== id));
  }, []);

  const value: RegistrosContextType = {
    registros,
    loading,
    error,
    cargarDatos,
    actualizarRegistro,
    agregarRegistro,
    eliminarRegistro,
  };

  return <RegistrosContext.Provider value={value}>{children}</RegistrosContext.Provider>;
};
